/**
 * A deposit nobody has answered for, in the Needs You queue (Blueprint §6, §13).
 *
 * It sits in the SAME queue as the staged suggestions — §13 is explicit that
 * there is one queue and never two — and it looks like the same kind of thing:
 * a card, an amount, and the actions that will actually work.
 *
 * What this card is careful NOT to do:
 *
 *   · Pre-select an answer. Highlighting "Income" would be defaulting it, which
 *     is the one thing this version exists not to do. Order is the only nudge.
 *   · Treat the bank's label as a fact. It is printed small, as what the bank
 *     said, and nothing acts on it.
 *   · Scold. "Needs a look" is a question, not a backlog.
 */

import { INFLOW_OPTIONS, bankHintText, depositDateText, depositTitle } from '../lib/deposit-copy';
import { formatMoney } from '../lib/envelope-math';
import type { ApiDeposit, ApiInflowType } from '../lib/api';

export interface AnswerHandlers {
  onAnswer: (deposit: ApiDeposit, type: ApiInflowType) => void;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

/**
 * The five answers as one-tap chips. Shared by the open question (no answer
 * yet) and by "Change" on a deposit that already has one — `current` marks the
 * existing answer for assistive technology without styling it as a default.
 */
export function createAnswerChips(
  deposit: ApiDeposit,
  handlers: AnswerHandlers,
  current: ApiInflowType | null = null,
): HTMLElement {
  const who = depositTitle(deposit);
  const amount = formatMoney(deposit.amountMinor);

  const group = el('div', 'sheet-chips answer-chips');
  group.setAttribute('role', 'group');
  group.setAttribute('aria-label', `What was ${amount} from ${who}?`);

  for (const option of INFLOW_OPTIONS) {
    const chip = el('button', 'chip', option.label);
    chip.type = 'button';
    chip.dataset.inflow = option.type;
    chip.setAttribute(
      'aria-label',
      `Mark ${amount} from ${who} as ${option.label}. ${option.hint}`,
    );
    if (current != null) chip.setAttribute('aria-pressed', String(option.type === current));
    chip.addEventListener('click', () => handlers.onAnswer(deposit, option.type));
    group.append(chip);
  }
  return group;
}

export function createDepositCard(deposit: ApiDeposit, handlers: AnswerHandlers): HTMLElement {
  const amount = formatMoney(deposit.amountMinor);
  const who = depositTitle(deposit);

  // NOT the `proposal` class: that means "a staged suggestion that moves money",
  // and the browser suite (and anything counting the queue's suggestions) relies
  // on it meaning exactly that. This card shares the proposal's LOOK through the
  // selectors in components.css, not its identity.
  const card = el('section', 'card deposit-card');
  card.dataset.txnKey = deposit.txnKey;

  card.append(el('div', 'label', 'Needs a look'));

  const figure = el('div', 'proposal-amount', amount);
  figure.setAttribute('aria-label', `${amount} from ${who}`);
  card.append(figure);

  card.append(el('div', 'proposal-route', `${who} · ${depositDateText(deposit.date)}`));

  // Two deposits from the same sender look identical without this — and the
  // invoice number the bank printed is often the one thing that says which
  // payment this is. Shown only when the sender is already the title, so the
  // same text is never printed twice.
  if (deposit.counterparty?.trim()) card.append(el('p', 'soft proposal-memo', deposit.description));

  const hint = bankHintText(deposit.bankHint);
  if (hint) card.append(el('p', 'soft proposal-memo', `Your bank labelled this: ${hint}`));

  card.append(el('p', 'deposit-question', 'What was this?'));
  card.append(createAnswerChips(deposit, handlers));

  return card;
}
